import React, { useContext, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import { previewOrderReceiptRead } from '../../clients/order_client';
import { logInfo } from '../../config_and_helpers/logger';
import { receiptPreviewFileError } from '../../utils/receiptRead';
import { ReceiptReadRecord } from './ReceiptReadReview';

const LOG_CONTEXT = 'ReceiptReadTestModal';

/**
 * Temporary home-page tool. Sends one scanned receipt or invoice and shows
 * the extraction. The scan is not attached to an order, and the read does
 * not change stock.
 *
 * @param {Object} props
 * @param {boolean} props.show
 * @param {Function} props.onHide
 */
const ReceiptReadTestModal = ({ show, onHide }) => {
  const { token } = useContext(AppContext);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [reading, setReading] = useState(false);
  const [readResult, setReadResult] = useState(null);

  const chooseFile = event => {
    const next = event.target.files?.[0] || null;
    setFile(next);
    setReadResult(null);
    setFileError(next ? receiptPreviewFileError(next) : '');
    logInfo(LOG_CONTEXT, 'receipt file chosen', {
      fileName: next?.name ?? '',
      fileSize: next?.size ?? null,
      fileType: next?.type ?? '',
    });
  };

  const readFile = async () => {
    const message = receiptPreviewFileError(file);
    if (message) {
      setFileError(message);
      return;
    }
    setReading(true);
    setReadResult(null);
    setFileError('');
    logInfo(LOG_CONTEXT, 'preview receipt', {
      fileName: file.name,
      fileSize: file.size,
    });
    const result = await previewOrderReceiptRead(token, file);
    setReading(false);
    setReadResult({ fileName: file.name, result });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      size="lg"
      dialogClassName="procure-modal-glass procure-modal-glass-form"
    >
      <Modal.Header closeButton className="procure-modal__header">
        <Modal.Title>Test receipt read</Modal.Title>
      </Modal.Header>
      <Modal.Body className="procure-modal__body">
        <p className="text-muted small">
          Temporary. Choose a scanned receipt or invoice. The extraction is
          shown here and is not saved on an order.
        </p>
        <Form
          onSubmit={event => {
            event.preventDefault();
            readFile();
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label htmlFor="receipt-read-test-file">Scanned receipt</Form.Label>
            <Form.Control
              id="receipt-read-test-file"
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
              onChange={chooseFile}
              disabled={reading}
            />
            <Form.Text className="text-muted">
              PDF, PNG, JPEG, WEBP, or GIF. Up to 10MB.
            </Form.Text>
          </Form.Group>
          <Button type="submit" variant="primary" disabled={!file || reading || !!fileError}>
            {reading ? 'Reading…' : 'Read'}
          </Button>
        </Form>
        {fileError ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {fileError}
          </Alert>
        ) : null}
        {reading ? (
          <div className="text-center py-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Reading receipt</span>
            </Spinner>
          </div>
        ) : null}
        {readResult?.result?.disabled ? (
          <Alert variant="info" className="mt-3 mb-0">
            Receipt reading is turned off.
          </Alert>
        ) : null}
        {readResult?.result?.success ? (
          <div className="glass-panel mt-3">
            <h3 className="h6">{readResult.fileName}</h3>
            <ReceiptReadRecord record={readResult.result.data} showMatch={false} />
          </div>
        ) : null}
        {readResult && !readResult.result?.success && !readResult.result?.disabled ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {(readResult.result?.messages || []).join(' ') ||
              'Could not read this receipt.'}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="procure-modal__footer">
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReceiptReadTestModal;
